import type { Tone } from "@/pages/admin/components/AdminUI";

export interface StatusMeta {
  label: string;
  tone: Tone;
}

const meta = (label: string, tone: Tone): StatusMeta => ({ label, tone });

export function commerceMeta(
  map: Record<string, StatusMeta>,
  key: string | null | undefined,
): StatusMeta {
  if (!key) return { label: "—", tone: "neutral" };
  return map[key] || { label: key, tone: "neutral" };
}

export function commerceOptions(map: Record<string, StatusMeta>): { value: string; label: string }[] {
  return Object.entries(map).map(([value, m]) => ({ value, label: m.label }));
}

/* ------------------------------- Experiences -------------------------------- */
export interface Experience {
  id: number;
  name: string;
  slug: string | null;
  farm_name: string | null;
  farmer_name: string | null;
  category: string | null;
  description: string | null;
  duration: string | null;
  price: number;
  capacity: number;
  age_range: string | null;
  audience: string | null;
  highlights: string[] | null;
  included: string | null;
  excluded: string | null;
  meeting_point: string | null;
  safety: string | null;
  status: string;
  featured: boolean;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export const EXPERIENCE_STATUS: Record<string, StatusMeta> = {
  draft: meta("Draft", "neutral"),
  review: meta("In Review", "accent"),
  published: meta("Published", "primary"),
  paused: meta("Paused", "accent"),
  archived: meta("Archived", "neutral"),
};

export const EXPERIENCE_CATEGORIES = [
  "Farm Life",
  "Farm-to-Plate",
  "Village Life",
  "Nature",
  "Adventure",
  "Farm Stay",
  "School",
  "Corporate",
  "Seasonal",
];

/* -------------------------------- Packages ---------------------------------- */
export interface ExperiencePackage {
  id: number;
  name: string;
  description: string | null;
  included: string | null;
  price: number;
  duration: string | null;
  capacity: number;
  availability: string | null;
  terms: string | null;
  status: string;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export const PACKAGE_STATUS: Record<string, StatusMeta> = {
  draft: meta("Draft", "neutral"),
  review: meta("In Review", "accent"),
  published: meta("Published", "primary"),
  paused: meta("Paused", "accent"),
  archived: meta("Archived", "neutral"),
};

/* -------------------------------- Activities -------------------------------- */
export interface Activity {
  id: number;
  name: string;
  duration: string | null;
  safety_level: string | null;
  age_restriction: string | null;
  price: number;
  capacity: number;
  farm_compatibility: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const ACTIVITY_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  paused: meta("Paused", "accent"),
  archived: meta("Archived", "neutral"),
};

export const SAFETY_LEVEL: Record<string, StatusMeta> = {
  Low: meta("Low", "primary"),
  Moderate: meta("Moderate", "accent"),
  High: meta("High", "accent"),
};

/* --------------------------------- Pricing ---------------------------------- */
export interface PricingRule {
  id: number;
  label: string;
  scope: string | null;
  target: string | null;
  audience: string | null;
  price: number;
  child_price: number;
  group_price: number;
  date_from: string | null;
  date_to: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const PRICING_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  paused: meta("Paused", "accent"),
  expired: meta("Expired", "neutral"),
};

export const PRICING_SCOPE: Record<string, StatusMeta> = {
  experience: meta("Experience", "secondary"),
  package: meta("Package", "secondary"),
  farm: meta("Farm", "secondary"),
};

export const AUDIENCE_TYPES = [
  "adult",
  "child",
  "group",
  "school",
  "corporate",
  "weekend",
  "weekday",
  "seasonal",
  "festival",
];

/* --------------------------------- Payments --------------------------------- */
export interface Payment {
  id: number;
  transaction_ref: string;
  booking_ref: string | null;
  visitor_name: string | null;
  farm_name: string | null;
  amount: number;
  gateway: string | null;
  method: string | null;
  status: string;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export const PAYMENT_STATUS: Record<string, StatusMeta> = {
  paid: meta("Paid", "primary"),
  pending: meta("Pending", "accent"),
  failed: meta("Failed", "neutral"),
  refunded: meta("Refunded", "secondary"),
};

/* --------------------------------- Refunds ---------------------------------- */
export interface Refund {
  id: number;
  refund_ref: string;
  booking_ref: string | null;
  visitor_name: string | null;
  amount: number;
  reason: string | null;
  requested_at: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const REFUND_STATUS: Record<string, StatusMeta> = {
  requested: meta("Requested", "accent"),
  approved: meta("Approved", "secondary"),
  processing: meta("Processing", "secondary"),
  completed: meta("Completed", "primary"),
  rejected: meta("Rejected", "neutral"),
};

/* --------------------------------- Coupons ---------------------------------- */
export interface Coupon {
  id: number;
  code: string;
  discount_type: string;
  discount_value: number;
  min_booking: number;
  max_discount: number;
  valid_from: string | null;
  valid_to: string | null;
  usage_limit: number;
  used_count: number;
  farm: string | null;
  experience: string | null;
  customer_type: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const COUPON_STATUS: Record<string, StatusMeta> = {
  active: meta("Active", "primary"),
  paused: meta("Paused", "accent"),
  expired: meta("Expired", "neutral"),
};

export const DISCOUNT_TYPE: Record<string, StatusMeta> = {
  percent: meta("Percentage", "secondary"),
  flat: meta("Flat", "secondary"),
};

export const CUSTOMER_TYPES = ["All", "Individual", "Family", "School", "College", "Corporate", "Tourist", "Group"];